/**
 * Entity Admin Onboarding Wizard — 5 steps.
 *
 *   1. Welcome / confirm profile
 *   2. Create first Campus (+ royalty % + subscription fee)
 *   3. Create first Store on that campus
 *   4. Invite team — at least 1 Cashier + optional Campus Correspondent
 *   5. Done
 *
 * Each step persists to its native endpoint (no shadow data) and then
 * advances /api/me/onboarding so progress survives a tab close.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL, formatApiError } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Sparkle, Buildings, Storefront, UsersThree, CheckCircle, X, ArrowRight, ArrowLeft, Spinner,
} from '@phosphor-icons/react';

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEPS = [
  { n: 1, label: 'Welcome',   icon: Sparkle      },
  { n: 2, label: 'Campus',    icon: Buildings    },
  { n: 3, label: 'Store',     icon: Storefront   },
  { n: 4, label: 'Team',      icon: UsersThree   },
  { n: 5, label: 'Done',      icon: CheckCircle  },
];

export default function EntityAdminWizard({ state, onAdvance, onSkip, onComplete, onDismiss }) {
  const step = Math.min(Math.max(state?.step || 1, 1), 5);
  const [campusId, setCampusId] = useState(state?.created_campus_id || null);

  const next = () => onAdvance(Math.min(step + 1, 5));
  const back = () => onAdvance(Math.max(step - 1, 1));

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A2540]/55 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="entity-admin-wizard">
      <div className="bg-[#FCFAF6] w-full sm:max-w-4xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col sm:flex-row max-h-[100vh] sm:max-h-[88vh]">
        {/* Left rail */}
        <aside className="sm:w-56 bg-[#0A2540] text-white p-5 flex sm:flex-col gap-3 sm:gap-5 shrink-0">
          <div className="hidden sm:block">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#C9A35B] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Setup
            </p>
            <p className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Get started in 5 quick steps
            </p>
          </div>
          <ol className="flex sm:flex-col gap-2 sm:gap-3 flex-1">
            {STEPS.map((s) => {
              const Ic = s.icon;
              const active = s.n === step;
              const done = s.n < step;
              return (
                <li
                  key={s.n}
                  className={`flex items-center gap-2 text-xs ${active ? 'opacity-100' : 'opacity-60'} ${done ? 'opacity-90' : ''}`}
                  data-testid={`wizard-step-${s.n}`}
                >
                  <span className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${
                    active
                      ? 'bg-[#C9A35B] text-[#0A2540]'
                      : done
                        ? 'bg-[#C9A35B]/30 text-[#C9A35B]'
                        : 'bg-white/10 text-white/60'
                  }`}>
                    <Ic size={14} weight="fill" />
                  </span>
                  <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {String(s.n).padStart(2, '0')} · {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Right pane */}
        <main className="flex-1 flex flex-col min-h-0">
          <header className="flex items-center justify-between p-4 sm:p-5 border-b border-[#EDE6D8]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Step {step} of 5
            </p>
            <button
              type="button"
              onClick={onDismiss}
              className="text-xs text-[#78716C] hover:text-[#0A2540] inline-flex items-center gap-1"
              data-testid="wizard-pause"
            >
              <X size={12} weight="bold" /> Pause &amp; continue later
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-5 sm:p-7">
            {step === 1 && <Step1Welcome onNext={next} />}
            {step === 2 && <Step2Campus  onCreated={(id) => { setCampusId(id); next(); }} onSkip={() => onSkip(2)} />}
            {step === 3 && <Step3Store   campusId={campusId} onCreated={next} onSkip={() => onSkip(3)} />}
            {step === 4 && <Step4Team    campusId={campusId} onNext={next} onSkip={() => onSkip(4)} />}
            {step === 5 && <Step5Done    onComplete={onComplete} />}
          </div>

          {/* Footer — Back button only (steps own their primary CTA) */}
          {step > 1 && step < 5 && (
            <footer className="border-t border-[#EDE6D8] px-5 py-3 flex items-center justify-between">
              <button
                type="button"
                onClick={back}
                className="text-xs text-[#78716C] hover:text-[#0A2540] inline-flex items-center gap-1 font-semibold"
                data-testid="wizard-back"
              >
                <ArrowLeft size={12} weight="bold" /> Back
              </button>
              <p className="text-[10px] text-[#78716C] font-mono">
                Closing now? Your progress is saved automatically.
              </p>
            </footer>
          )}
        </main>
      </div>
    </div>
  );
}

/* ───── Step 1 — Welcome ───── */
function Step1Welcome({ onNext }) {
  const { user } = useAuth();
  return (
    <div className="max-w-xl">
      <div className="w-12 h-12 rounded-2xl bg-[#C9A35B]/20 text-[#C9A35B] flex items-center justify-center mb-4">
        <Sparkle size={22} weight="fill" />
      </div>
      <h2 className="text-2xl font-bold text-[#1A1D1C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Welcome to Ecowater Cloud, {(user?.fullname || user?.username || '').split(' ')[0] || 'there'}!
      </h2>
      <p className="text-sm text-[#78716C] leading-relaxed">
        This quick setup walks you through the four things every new entity needs:
        a campus, a store, royalty &amp; subscription terms, and your first team
        member. The whole tour takes about 3 minutes, and your progress is saved
        if you need to step away.
      </p>
      <ul className="mt-5 space-y-2 text-sm text-[#1A1D1C]">
        {['Create your first campus', 'Open your first store', 'Configure royalty &amp; subscription fee', 'Invite a cashier'].map((t, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-5 h-5 shrink-0 rounded-full bg-[#0A2540]/5 text-[#0A2540] text-[10px] font-bold flex items-center justify-center" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{i + 1}</span>
            <span dangerouslySetInnerHTML={{ __html: t }} />
          </li>
        ))}
      </ul>
      <div className="mt-7">
        <Button onClick={onNext} data-testid="wizard-step1-next" className="px-5">
          Let's go <ArrowRight size={14} className="ml-2" weight="bold" />
        </Button>
      </div>
    </div>
  );
}

/* ───── Step 2 — Campus + royalty + sub fee ───── */
function Step2Campus({ onCreated, onSkip }) {
  const [name, setName]               = useState('');
  const [shortName, setShortName]     = useState('');
  const [subFee, setSubFee]           = useState('');
  const [royalty, setRoyalty]         = useState('');
  const [saving, setSaving]           = useState(false);
  const { user } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Campus name is required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/campuses`,
        {
          campus_name: name.trim(),
          short_name: shortName.trim() || name.trim().slice(0, 8).toUpperCase(),
          entity_id: user?.entity_id,
          subscription_fee: parseFloat(subFee) || 0,
          royalty: parseFloat(royalty) || 0,
        },
        { headers: authHeaders() }
      );
      toast.success('Campus created');
      onCreated(String(data.id || data.middleware_id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-xl space-y-5">
      <div>
        <div className="w-10 h-10 rounded-xl bg-[#0A2540]/10 text-[#0A2540] flex items-center justify-center mb-3">
          <Buildings size={20} weight="fill" />
        </div>
        <h2 className="text-xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Create your first campus
        </h2>
        <p className="text-sm text-[#78716C] mt-1">
          A campus is your top-level unit — a university, corporate complex, or housing estate.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Campus name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Islamic University in Uganda" autoFocus data-testid="onb-campus-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Short code <span className="text-[10px] text-[#78716C]">(optional)</span></Label>
          <Input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="IUIU" data-testid="onb-campus-short" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Subscription fee (per subscriber)</Label>
          <Input type="number" min="0" step="any" value={subFee} onChange={(e) => setSubFee(e.target.value)} placeholder="5000" data-testid="onb-campus-fee" />
          <p className="text-[10px] text-[#78716C]">Charged once when a subscriber joins on the Fee plan. Set to 0 if free.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Royalty %</Label>
          <Input type="number" min="0" max="100" step="any" value={royalty} onChange={(e) => setRoyalty(e.target.value)} placeholder="5" data-testid="onb-campus-royalty" />
          <p className="text-[10px] text-[#78716C]">Percentage of refill revenue that goes back to the campus. Stores inherit this by default.</p>
        </div>
      </div>

      <div className="pt-3 flex items-center justify-between gap-3">
        <button type="button" onClick={onSkip} className="text-xs text-[#78716C] hover:text-[#0A2540] font-semibold" data-testid="onb-skip-2">
          Skip — I'll do this later
        </button>
        <Button type="submit" disabled={saving} data-testid="onb-create-campus">
          {saving ? <><Spinner className="animate-spin mr-2" size={14} /> Creating…</> : <>Create campus <ArrowRight size={14} className="ml-2" weight="bold" /></>}
        </Button>
      </div>
    </form>
  );
}

/* ───── Step 3 — Store ───── */
function Step3Store({ campusId, onCreated, onSkip }) {
  const [name, setName]               = useState('');
  const [code, setCode]               = useState('');
  const [price, setPrice]             = useState('');
  const [royalty, setRoyalty]         = useState('');
  const [saving, setSaving]           = useState(false);
  const [campusOptions, setCampusOptions] = useState([]);
  const [chosenCampus, setChosenCampus]   = useState(campusId || '');
  const { user } = useAuth();

  // Fetch campus options in case the user skipped step 2 and we need to fall
  // back to an existing campus.
  useEffect(() => {
    if (!user?.entity_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/campuses?entity_id=${user.entity_id}`, { headers: authHeaders() });
        if (!cancelled) {
          setCampusOptions(Array.isArray(data) ? data : []);
          if (!chosenCampus && data?.length) setChosenCampus(String(data[0].id));
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!chosenCampus) { toast.error('Pick a campus first.'); return; }
    if (!name.trim()) { toast.error('Store name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/stores`, {
        store_name: name.trim(),
        store_code: code.trim() || undefined,
        campus_id: String(chosenCampus),
        selling_price: parseFloat(price) || 0,
        royalty: parseFloat(royalty) || 0,
      }, { headers: authHeaders() });
      toast.success('Store created');
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-xl space-y-5">
      <div>
        <div className="w-10 h-10 rounded-xl bg-[#059669]/10 text-[#059669] flex items-center justify-center mb-3">
          <Storefront size={20} weight="fill" />
        </div>
        <h2 className="text-xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Open your first store
        </h2>
        <p className="text-sm text-[#78716C] mt-1">
          Stores are where refills happen. Each store sits inside a campus and has its own selling price and royalty.
        </p>
      </div>

      {campusOptions.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Campus</Label>
          <select
            value={chosenCampus}
            onChange={(e) => setChosenCampus(e.target.value)}
            className="w-full h-10 rounded-lg border border-[#EDE6D8] bg-white px-3 text-sm focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/15 transition"
            data-testid="onb-store-campus"
          >
            {campusOptions.map((c) => (<option key={c.id} value={String(c.id)}>{c.campus_name}</option>))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Store name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Main Campus Store" autoFocus data-testid="onb-store-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Store code <span className="text-[10px]">(optional)</span></Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ST-MC-01" data-testid="onb-store-code" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Selling price (per litre) *</Label>
          <Input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="300" data-testid="onb-store-price" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#78716C]">Royalty % <span className="text-[10px]">(overrides campus)</span></Label>
          <Input type="number" min="0" max="100" step="any" value={royalty} onChange={(e) => setRoyalty(e.target.value)} placeholder="5" data-testid="onb-store-royalty" />
        </div>
      </div>

      <div className="pt-3 flex items-center justify-between gap-3">
        <button type="button" onClick={onSkip} className="text-xs text-[#78716C] hover:text-[#0A2540] font-semibold" data-testid="onb-skip-3">
          Skip
        </button>
        <Button type="submit" disabled={saving} data-testid="onb-create-store">
          {saving ? <><Spinner className="animate-spin mr-2" size={14} /> Creating…</> : <>Create store <ArrowRight size={14} className="ml-2" weight="bold" /></>}
        </Button>
      </div>
    </form>
  );
}

/* ───── Step 4 — Invite first cashier ───── */
function Step4Team({ campusId, onNext, onSkip }) {
  const [fullname, setFullname] = useState('');
  const [email, setEmail]       = useState('');
  const [mobile, setMobile]     = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving]     = useState(false);
  const { user } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (!fullname || !email || !mobile || !password) {
      toast.error('Please fill every field.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/users`, {
        password,
        email,
        fullname,
        role: 'campus_agent',
        entity_id: user?.entity_id,
        mobile,
      }, { headers: authHeaders() });
      toast.success('Cashier invited');
      onNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-xl space-y-5">
      <div>
        <div className="w-10 h-10 rounded-xl bg-[#7C3AED]/10 text-[#7C3AED] flex items-center justify-center mb-3">
          <UsersThree size={20} weight="fill" />
        </div>
        <h2 className="text-xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Invite your first cashier
        </h2>
        <p className="text-sm text-[#78716C] mt-1">
          Cashiers are the people behind the counter at each store. They'll sign in to the dedicated Cashier app to register subscribers, recharge wallets, and renew cards.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className="text-xs text-[#78716C]">Full name *</Label><Input value={fullname} onChange={(e) => setFullname(e.target.value)} data-testid="onb-cashier-name" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-[#78716C]">Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="onb-cashier-email" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-[#78716C]">Mobile *</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="2557…" data-testid="onb-cashier-mobile" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-[#78716C]">Temporary password *</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="onb-cashier-password" /></div>
      </div>
      <p className="text-[10px] text-[#78716C]">The cashier will be asked to change this password the first time they sign in.</p>

      <div className="pt-3 flex items-center justify-between gap-3">
        <button type="button" onClick={onSkip} className="text-xs text-[#78716C] hover:text-[#0A2540] font-semibold" data-testid="onb-skip-4">
          Skip — I'll invite people later
        </button>
        <Button type="submit" disabled={saving} data-testid="onb-create-cashier">
          {saving ? <><Spinner className="animate-spin mr-2" size={14} /> Inviting…</> : <>Invite cashier <ArrowRight size={14} className="ml-2" weight="bold" /></>}
        </Button>
      </div>
    </form>
  );
}

/* ───── Step 5 — Done ───── */
function Step5Done({ onComplete }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-xl text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center mb-4">
        <CheckCircle size={32} weight="fill" />
      </div>
      <h2 className="text-2xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        You're all set! 🎉
      </h2>
      <p className="text-sm text-[#78716C] mt-2 leading-relaxed">
        Your entity is ready to go. From here you can register subscribers, accept refills, and watch revenue roll up to the dashboard in real time.
      </p>
      <p className="text-xs text-[#78716C] mt-4">
        Need to revisit any step? Open the avatar menu in the top-right and pick <em>Setup Guide</em> any time.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={async () => { await onComplete(); navigate('/'); }}
          data-testid="onb-finish"
          className="px-5"
        >
          Open dashboard <ArrowRight size={14} className="ml-2" weight="bold" />
        </Button>
      </div>
    </div>
  );
}
